import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, TextField, MenuItem, FormControl, InputLabel,
  Select, Chip, Grid, Alert, Snackbar, CircularProgress, Tabs, Tab,
  FormControlLabel, Checkbox, InputAdornment, Toolbar, FormGroup, Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Business as BusinessIcon, Group as GroupIcon, Business as CompanyIcon, Visibility, VisibilityOff, FilterList as FilterListIcon, BarChart as BarChartIcon, People as PeopleIcon, Refresh as RefreshIcon, CalendarToday as CalendarTodayIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const UsersManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    companyId: '',
    active: true,
    freewheelMode: false,
    expirationDate: '',
    phoneNumber: '',
    // Nuovi campi per permessi sezioni
    accessRentals: true,
    accessMaintenance: true,
    accessSales: true,
    accessShop: false,
    accessAccounting: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [companyError, setCompanyError] = useState('');
  
  // Gestione tab
  const [currentTab, setCurrentTab] = useState(0);
  
  // Stato per mostrare/nascondere password
  const [showPassword, setShowPassword] = useState(false);
  
  // Filtri per la tabella utenti
  const [userStatusFilter, setUserStatusFilter] = useState('active');
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    role: '',
    company: ''
  });
  
  // Stati per gestione aziende
  const [openCompanyEditDialog, setOpenCompanyEditDialog] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    rank: 1,
    active: true,
    // Dati azienda
    address: '',
    city: '',
    postalCode: '',
    province: '',
    phone: '',
    email: '',
    vatNumber: '',
    shippingName: '',
    shippingAddress: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingProvince: '',
    shippingPhone: ''
  });

  // Stati per statistiche aziende
  const [companiesStats, setCompaniesStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedCompanyUsers, setSelectedCompanyUsers] = useState([]);
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewingCompanyId, setRenewingCompanyId] = useState(null);
  const [openModulesDialog, setOpenModulesDialog] = useState(false);
  const [editingCompanyModules, setEditingCompanyModules] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  
  // Filtro per aziende (all, active, inactive)
  const [companyFilter, setCompanyFilter] = useState('all');


  // Ruoli disponibili in base al ruolo dell'utente corrente
  const getAvailableRoles = () => {
    if (user?.role === 'supersaiyan' || user?.role === 'superadmin') {
      return ['user', 'mechanic', 'admin', 'manager', 'superadmin', 'supersaiyan'];
    } else if (user?.role === 'admin') {
      return ['user', 'mechanic', 'manager'];
    }
    return [];
  };

  // Carica gli utenti e le aziende
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Carica gli utenti
        const usersResponse = await api.get('/users');
        setUsers(usersResponse.data);
        
        // Carica le aziende
        try {
          const companiesResponse = await api.get('/companies');
          setCompanies(companiesResponse.data);
        } catch (companyErr) {
          console.error('Errore nel caricamento delle aziende:', companyErr);
          // Non bloccare il caricamento degli utenti se le aziende falliscono
          // Impostiamo una lista di aziende vuota
          setCompanies([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Errore nel caricamento dei dati:', err);
        setError('Errore nel caricamento degli utenti. ' + 
          (err.response?.data?.msg || err.message || 'Riprova più tardi.'));
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Carica statistiche aziende quando si apre la tab Statistiche
  useEffect(() => {
    const fetchCompaniesStats = async () => {
      if (currentTab === 2 && (user?.role === 'superadmin' || user?.role === 'supersaiyan')) {
        setLoadingStats(true);
        try {
          const response = await api.get('/companies-stats');
          setCompaniesStats(response.data);
        } catch (err) {
          console.error('Errore nel caricamento statistiche aziende:', err);
          setCompaniesStats([]);
        } finally {
          setLoadingStats(false);
        }
      }
    };

    if (user) {
      fetchCompaniesStats();
    }
  }, [currentTab, user]);


  // Effetto aggiuntivo per monitorare i cambiamenti di stato per il debugging
  useEffect(() => {
    if (openDialog) {
      console.log('Dialog aperto con utente:', currentUser);
    }
  }, [openDialog, currentUser]);

  // Gestione degli input del form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
      companyId: user?.companyId || '',
      active: true,
      freewheelMode: false,
      expirationDate: '',
      phoneNumber: '',
      accessRentals: true,
      accessMaintenance: true,
      accessSales: true,
      accessShop: false,
      accessAccounting: false
    });
  };

  const handleOpenAddDialog = () => {
    setCurrentUser(null);
    resetForm();
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (userData) => {
    try {
      console.log('Apertura dialog modifica per utente:', userData);
      // Prima impostiamo i dati
      setFormData({
        name: userData.name,
        email: userData.email,
        password: '', // Non mostro la password corrente
        role: userData.role,
        companyId: userData.companyId || '',
        active: userData.active,
        freewheelMode: userData.freewheelMode || false,
        expirationDate: userData.expirationDate ? new Date(userData.expirationDate).toISOString().split('T')[0] : '',
        phoneNumber: userData.phoneNumber || '',
        accessRentals: userData.accessRentals !== undefined ? userData.accessRentals : true,
        accessMaintenance: userData.accessMaintenance !== undefined ? userData.accessMaintenance : true,
        accessSales: userData.accessSales !== undefined ? userData.accessSales : true,
        accessShop: userData.accessShop !== undefined ? userData.accessShop : false,
        accessAccounting: userData.accessAccounting !== undefined ? userData.accessAccounting : false
      });
      
      // Poi impostiamo l'utente corrente
      setCurrentUser(userData);
      
      // Infine apriamo il dialog
      console.log('Apertura dialog...');
      setTimeout(() => {
        setOpenDialog(true);
        console.log('Dialog aperto');
      }, 0);
    } catch (error) {
      console.error('Errore durante l\'apertura del dialog:', error);
    }
  };

  const handleOpenDeleteDialog = (userData) => {
    setCurrentUser(userData);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenDeleteDialog(false);
    setCurrentUser(null);
  };

  // Salva un nuovo utente o aggiorna un utente esistente
  const handleSaveUser = async () => {
    try {
      if (currentUser) {
        // Aggiorna utente esistente
        await api.put(`/users/${currentUser.id}`, formData);
      } else {
        // Crea nuovo utente
        await api.post('/users', formData);
      }
      
      // Ricarica la lista degli utenti
      const response = await api.get('/users');
      setUsers(response.data);
      
      setSnackbar({
        open: true,
        message: 'Utente salvato con successo!',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (err) {
      console.error('Errore nel salvataggio dell\'utente:', err);
      
      let errorMessage = 'Impossibile salvare l\'utente';
      
      if (err.response?.data) {
        // Gestisci errori di validazione (array di errori)
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errorMessages = err.response.data.errors.map(e => e.msg || e.message).join(', ');
          errorMessage = errorMessages;
        }
        // Gestisci errori singoli (messaggio diretto)
        else if (err.response.data.msg) {
          errorMessage = err.response.data.msg;
        }
      }
      
      setSnackbar({
        open: true,
        message: `Errore: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  // Elimina un utente
  const handleDeleteUser = async () => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      try {
        await api.delete(`/users/${currentUser.id}`);
        
        // Ricarica la lista degli utenti
        const response = await api.get('/users');
        setUsers(response.data);
        
        setSnackbar({
          open: true,
          message: 'Utente eliminato con successo!',
          severity: 'success'
        });
        handleCloseDialog();
      } catch (err) {
        console.error('Errore nell\'eliminazione dell\'utente:', err);
        setSnackbar({
          open: true,
          message: `Errore: ${err.response?.data?.msg || 'Impossibile eliminare l\'utente'}`,
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Controlla se l'utente corrente può modificare un altro utente
  const canEditUser = (targetUser) => {
    if (!user) return false;
    
    // SuperSaiyan e SuperAdmin possono modificare tutti
    if (user.role === 'supersaiyan' || user.role === 'superadmin') return true;
    
    // Admin può modificare solo utenti della propria azienda con ruolo inferiore
    if (user.role === 'admin') {
      return targetUser.companyId === user.companyId && 
        (targetUser.role === 'user' || targetUser.role === 'mechanic');
    }
    
    return false;
  };

  // Ottieni il nome dell'azienda da ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'N/A';
  };

  // Rendering dello stato dell'utente
  const renderUserStatus = (active) => {
    return active ? 
      <Chip label="Attivo" color="success" size="small" /> : 
      <Chip label="Inattivo" color="error" size="small" />;
  };

  const handleOpenCompanyDialog = () => {
    setNewCompanyName('');
    setCompanyError('');
    setOpenCompanyDialog(true);
  };

  const handleCloseCompanyDialog = () => {
    setOpenCompanyDialog(false);
  };

  // Gestione tab
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Gestione form aziende
  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenCompanyEditDialog = async (company = null) => {
    if (company) {
      setCurrentCompany(company);
      // Carica i dati azienda da CompanySettings per l'azienda selezionata
      try {
        const settingsResponse = await api.get(`/company-settings?companyId=${company.id}`);
        const settings = settingsResponse.data || {};
        setCompanyFormData({
          name: company.name,
          rank: company.rank || 1,
          active: company.active !== false,
          address: settings.companyAddress || '',
          city: settings.companyCity || '',
          postalCode: settings.companyPostalCode || '',
          province: settings.companyProvince || '',
          phone: settings.companyPhone || '',
          email: settings.companyEmail || '',
          vatNumber: settings.companyVatNumber || '',
          shippingName: settings.shippingName || '',
          shippingAddress: settings.shippingAddress || '',
          shippingCity: settings.shippingCity || '',
          shippingPostalCode: settings.shippingPostalCode || '',
          shippingProvince: settings.shippingProvince || '',
          shippingPhone: settings.shippingPhone || ''
        });
      } catch (err) {
        console.error('Errore nel caricamento impostazioni azienda:', err);
        setCompanyFormData({
          name: company.name,
          rank: company.rank || 1,
          active: company.active !== false,
          address: '',
          city: '',
          postalCode: '',
          province: '',
          phone: '',
          email: '',
          vatNumber: '',
          shippingName: '',
          shippingAddress: '',
          shippingCity: '',
          shippingPostalCode: '',
          shippingProvince: '',
          shippingPhone: ''
        });
      }
    } else {
      setCurrentCompany(null);
      setCompanyFormData({
        name: '',
        rank: 1,
        active: true,
        address: '',
        city: '',
        postalCode: '',
        province: '',
        phone: '',
        email: '',
        vatNumber: '',
        shippingName: '',
        shippingAddress: '',
        shippingCity: '',
        shippingPostalCode: '',
        shippingProvince: '',
        shippingPhone: ''
      });
    }
    setOpenCompanyEditDialog(true);
  };

  const handleCloseCompanyEditDialog = () => {
    setOpenCompanyEditDialog(false);
    setCurrentCompany(null);
    setCompanyFormData({
      name: '',
      rank: 1
    });
  };

  const handleSaveCompany = async () => {
    try {
      if (!companyFormData.vatNumber) {
        setSnackbar({
          open: true,
          message: 'La partita IVA è obbligatoria',
          severity: 'error'
        });
        return;
      }

      let companyId;
      if (currentCompany) {
        // Aggiorna azienda esistente
        const response = await api.put(`/companies/${currentCompany.id}`, {
          name: companyFormData.name,
          rank: companyFormData.rank,
          active: companyFormData.active,
          vatNumber: companyFormData.vatNumber
        });
        companyId = currentCompany.id;
      } else {
        // Crea nuova azienda
        const response = await api.post('/companies', {
          name: companyFormData.name,
          rank: companyFormData.rank,
          active: companyFormData.active,
          vatNumber: companyFormData.vatNumber
        });
        companyId = response.data.id;
      }

      // Salva i dati azienda in CompanySettings usando il companyId corretto
      const companySettings = {
        companyName: companyFormData.name,
        companyAddress: companyFormData.address,
        companyCity: companyFormData.city,
        companyPostalCode: companyFormData.postalCode,
        companyProvince: companyFormData.province,
        companyPhone: companyFormData.phone,
        companyEmail: companyFormData.email,
        companyVatNumber: companyFormData.vatNumber,
        shippingName: companyFormData.shippingName,
        shippingAddress: companyFormData.shippingAddress,
        shippingCity: companyFormData.shippingCity,
        shippingPostalCode: companyFormData.shippingPostalCode,
        shippingProvince: companyFormData.shippingProvince,
        shippingPhone: companyFormData.shippingPhone
      };

      // Salva i dati azienda per l'azienda selezionata
      await api.patch('/company-settings', {
        ...companySettings,
        companyId
      });
      
      // Ricarica la lista delle aziende
      const response = await api.get('/companies');
      setCompanies(response.data);
      
      setSnackbar({
        open: true,
        message: 'Azienda salvata con successo!',
        severity: 'success'
      });
      handleCloseCompanyEditDialog();
    } catch (err) {
      console.error('Errore nel salvataggio dell\'azienda:', err);
      setSnackbar({
        open: true,
        message: `Errore: ${err.response?.data?.msg || 'Impossibile salvare l\'azienda'}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa azienda?')) {
      try {
        await api.delete(`/companies/${companyId}`);
        
        // Ricarica la lista delle aziende
        const response = await api.get('/companies');
        setCompanies(response.data);
        
        setSnackbar({
          open: true,
          message: 'Azienda eliminata con successo!',
          severity: 'success'
        });
      } catch (err) {
        console.error('Errore nell\'eliminazione dell\'azienda:', err);
        setSnackbar({
          open: true,
          message: `Errore: ${err.response?.data?.msg || 'Impossibile eliminare l\'azienda'}`,
          severity: 'error'
        });
      }
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) {
      setCompanyError('Il nome dell\'azienda è obbligatorio');
      return;
    }

    try {
      const response = await api.post('/companies', { name: newCompanyName.trim() });
      const newCompany = response.data;
      
      // Aggiorna la lista delle aziende
      setCompanies([...companies, newCompany]);
      
      // Seleziona automaticamente la nuova azienda
      setFormData(prev => ({ ...prev, companyId: newCompany.id }));
      
      setSnackbar({
        open: true,
        message: 'Azienda creata con successo!',
        severity: 'success'
      });
      
      handleCloseCompanyDialog();
    } catch (err) {
      console.error('Errore nella creazione dell\'azienda:', err);
      setCompanyError(err.response?.data?.msg || 'Errore nella creazione dell\'azienda');
    }
  };

  if (loading) {
    return <Typography>Caricamento...</Typography>;
  }

  // Verifica permessi
  if (!['admin', 'superadmin', 'supersaiyan'].includes(user?.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Gestione Utenti e Aziende
      </Typography>
      
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab 
            label="Utenti" 
            icon={<GroupIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Aziende" 
            icon={<CompanyIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Statistiche" 
            icon={<BarChartIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Box>
          {/* Header Utenti */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Lista Utenti
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Nuovo Utente
            </Button>
          </Box>

          {/* Filtri */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Stato Utente</InputLabel>
                    <Select
                      value={userStatusFilter}
                      label="Stato Utente"
                      onChange={(e) => setUserStatusFilter(e.target.value)}
                    >
                      <MenuItem value="active">Solo Attivi</MenuItem>
                      <MenuItem value="all">Tutti</MenuItem>
                      <MenuItem value="inactive">Solo Inattivi</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Filtra per Nome"
                    value={filters.name}
                    onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Filtra per Email"
                    value={filters.email}
                    onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Filtra per Telefono"
                    value={filters.phoneNumber}
                    onChange={(e) => setFilters(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filtra per Ruolo</InputLabel>
                    <Select
                      value={filters.role}
                      label="Filtra per Ruolo"
                      onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      {[...new Set(users.map(u => u.role))].map(role => (
                        <MenuItem key={role} value={role}>{role}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filtra per Azienda</InputLabel>
                    <Select
                      value={filters.company}
                      label="Filtra per Azienda"
                      onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
                    >
                      <MenuItem value="">Tutte</MenuItem>
                      {companies.map(company => (
                        <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefono</TableCell>
                <TableCell>Ruolo</TableCell>
                <TableCell>Azienda</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Scadenza</TableCell>
                <TableCell>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                let filteredUsers = users;
                if (userStatusFilter === 'active') {
                  filteredUsers = users.filter(u => u.active);
                } else if (userStatusFilter === 'inactive') {
                  filteredUsers = users.filter(u => !u.active);
                }
                
                // Applica filtri
                if (filters.name) {
                  filteredUsers = filteredUsers.filter(u => 
                    u.name.toLowerCase().includes(filters.name.toLowerCase())
                  );
                }
                if (filters.email) {
                  filteredUsers = filteredUsers.filter(u => 
                    u.email.toLowerCase().includes(filters.email.toLowerCase())
                  );
                }
                if (filters.phoneNumber) {
                  filteredUsers = filteredUsers.filter(u => 
                    (u.phoneNumber || '').includes(filters.phoneNumber)
                  );
                }
                if (filters.role) {
                  filteredUsers = filteredUsers.filter(u => u.role === filters.role);
                }
                if (filters.company) {
                  filteredUsers = filteredUsers.filter(u => u.companyId === parseInt(filters.company));
                }
                
                if (filteredUsers.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Nessun utente trovato
                      </TableCell>
                    </TableRow>
                  );
                }
                
                return filteredUsers.map((userData) => (
                  <TableRow key={userData.id}>
                    <TableCell>{userData.name}</TableCell>
                    <TableCell>{userData.email}</TableCell>
                    <TableCell>{userData.phoneNumber || '-'}</TableCell>
                    <TableCell>{userData.role}</TableCell>
                    <TableCell>{getCompanyName(userData.companyId)}</TableCell>
                    <TableCell>{renderUserStatus(userData.active)}</TableCell>
                    <TableCell>
                      {userData.expirationDate 
                        ? new Date(userData.expirationDate).toLocaleDateString('it-IT')
                        : 'Nessuna scadenza'}
                    </TableCell>
                    <TableCell>
                      {canEditUser(userData) && (
                        <>
                          <IconButton 
                            color="primary" 
                            onClick={() => {
                              console.log('Cliccato edit per:', userData.name);
                              handleOpenEditDialog(userData);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleOpenDeleteDialog(userData)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
          )}
        </Box>
      )}

      {/* Tab Aziende */}
      {currentTab === 1 && (
        <Box>
          {/* Header Aziende */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" component="h2">
              Lista Aziende
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filtra Aziende</InputLabel>
                <Select
                  value={companyFilter}
                  label="Filtra Aziende"
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <MenuItem value="all">Tutte</MenuItem>
                  <MenuItem value="active">Solo Attive</MenuItem>
                  <MenuItem value="inactive">Solo Inattive</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenCompanyEditDialog()}
              >
                Nuova Azienda
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome Azienda</TableCell>
                    <TableCell>Rank</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Utenti</TableCell>
                    <TableCell>Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Applica il filtro alle aziende
                    let filteredCompanies = companies;
                    if (companyFilter === 'active') {
                      filteredCompanies = companies.filter(c => c.active !== false);
                    } else if (companyFilter === 'inactive') {
                      filteredCompanies = companies.filter(c => c.active === false);
                    }
                    
                    if (filteredCompanies.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Nessuna azienda trovata
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return filteredCompanies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell>{company.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`Rank ${company.rank || 1}`} 
                            color={company.rank === 1 ? 'success' : company.rank <= 3 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={company.active !== false ? 'Attiva' : 'Inattiva'} 
                            color={company.active !== false ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {users.filter(u => u.companyId === company.id).length} utenti
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleOpenCompanyEditDialog(company)}
                            title="Modifica azienda"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteCompany(company.id)}
                            title="Elimina azienda"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab Statistiche */}
      {currentTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" component="h2">
              Statistiche Aziende
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filtra Aziende</InputLabel>
              <Select
                value={companyFilter}
                label="Filtra Aziende"
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <MenuItem value="all">Tutte</MenuItem>
                <MenuItem value="active">Solo Attive</MenuItem>
                <MenuItem value="inactive">Solo Inattive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {loadingStats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Azienda</strong></TableCell>
                    <TableCell><strong>Data Creazione</strong></TableCell>
                    <TableCell><strong>Importo Abbonamento</strong></TableCell>
                    <TableCell><strong>Data Scadenza</strong></TableCell>
                    <TableCell><strong>Moduli Acquistati</strong></TableCell>
                    <TableCell><strong>Utenti</strong></TableCell>
                    <TableCell><strong>Azioni</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Applica il filtro alle statistiche aziende
                    let filteredCompaniesStats = companiesStats;
                    if (companyFilter === 'active') {
                      filteredCompaniesStats = companiesStats.filter(c => c.active !== false);
                    } else if (companyFilter === 'inactive') {
                      filteredCompaniesStats = companiesStats.filter(c => c.active === false);
                    }
                    
                    if (filteredCompaniesStats.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            Nessuna azienda trovata
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return filteredCompaniesStats.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {company.name}
                            </Typography>
                            <Chip
                              label={company.active !== false ? 'Attiva' : 'Inattiva'}
                              color={company.active !== false ? 'success' : 'error'}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {company.accountCreationDate
                            ? new Date(company.accountCreationDate).toLocaleDateString('it-IT')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold" color="primary">
                            €{company.subscriptionAmount.toFixed(2)}/mese
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {company.expirationDate ? (
                            <Box>
                              <Typography variant="body2">
                                {new Date(company.expirationDate).toLocaleDateString('it-IT')}
                              </Typography>
                              {new Date(company.expirationDate) < new Date() && (
                                <Chip
                                  label="Scaduto"
                                  color="error"
                                  size="small"
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Nessuna scadenza
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {company.purchasedModules && company.purchasedModules.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {company.purchasedModules.map((module, idx) => (
                                <Chip
                                  key={idx}
                                  label={module}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Nessun modulo
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            startIcon={<PeopleIcon />}
                            size="small"
                            onClick={() => {
                              setSelectedCompanyUsers(company.users || []);
                              setOpenUsersDialog(true);
                            }}
                          >
                            {company.usersCount} utenti
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setEditingCompanyModules(company);
                                setSelectedModules(company.moduleIds || []);
                                setOpenModulesDialog(true);
                              }}
                              title="Modifica Moduli"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setRenewingCompanyId(company.id);
                                setOpenRenewDialog(true);
                              }}
                              title="Rinnova Abbonamento"
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Dialog per vedere gli utenti di un'azienda */}
          <Dialog open={openUsersDialog} onClose={() => setOpenUsersDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              Utenti dell'Azienda
            </DialogTitle>
            <DialogContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Nome</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Ruolo</strong></TableCell>
                      <TableCell><strong>Scadenza</strong></TableCell>
                      <TableCell><strong>Stato</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCompanyUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Nessun utente trovato
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedCompanyUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.role}</TableCell>
                          <TableCell>
                            {user.expirationDate
                              ? new Date(user.expirationDate).toLocaleDateString('it-IT')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.active ? 'Attivo' : 'Disattivato'}
                              color={user.active ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenUsersDialog(false)}>Chiudi</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog per rinnovare abbonamento */}
          <Dialog open={openRenewDialog} onClose={() => setOpenRenewDialog(false)}>
            <DialogTitle>Rinnova Abbonamento</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Seleziona il numero di mesi da aggiungere all'abbonamento. La data di scadenza verrà aggiornata per tutti gli utenti attivi dell'azienda.
              </DialogContentText>
              <TextField
                label="Mesi"
                type="number"
                value={renewMonths}
                onChange={(e) => setRenewMonths(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 12 }}
                fullWidth
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenRenewDialog(false)}>Annulla</Button>
              <Button
                onClick={async () => {
                  try {
                    await api.post(`/companies-stats/${renewingCompanyId}/renew`, {
                      months: renewMonths
                    });
                    setSnackbar({
                      open: true,
                      message: `Abbonamento rinnovato per ${renewMonths} ${renewMonths === 1 ? 'mese' : 'mesi'}`,
                      severity: 'success'
                    });
                    setOpenRenewDialog(false);
                    // Ricarica le statistiche
                    const response = await api.get('/companies-stats');
                    setCompaniesStats(response.data);
                  } catch (err) {
                    setSnackbar({
                      open: true,
                      message: err.response?.data?.msg || 'Errore nel rinnovo abbonamento',
                      severity: 'error'
                    });
                  }
                }}
                variant="contained"
                color="primary"
              >
                Conferma
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dialog per modificare moduli */}
          <Dialog open={openModulesDialog} onClose={() => setOpenModulesDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Modifica Moduli - {editingCompanyModules?.name}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Seleziona i moduli attivi per questa azienda. L'importo abbonamento verrà ricalcolato automaticamente.
              </DialogContentText>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedModules.includes('maintenance')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedModules([...selectedModules, 'maintenance']);
                        } else {
                          setSelectedModules(selectedModules.filter(m => m !== 'maintenance'));
                        }
                      }}
                    />
                  }
                  label="Manutenzioni"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedModules.includes('shop')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedModules([...selectedModules, 'shop']);
                        } else {
                          setSelectedModules(selectedModules.filter(m => m !== 'shop'));
                        }
                      }}
                    />
                  }
                  label="Shop B2B"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedModules.includes('sales')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedModules([...selectedModules, 'sales']);
                        } else {
                          setSelectedModules(selectedModules.filter(m => m !== 'sales'));
                        }
                      }}
                    />
                  }
                  label="Vendite al banco"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedModules.includes('rental')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedModules([...selectedModules, 'rental']);
                        } else {
                          setSelectedModules(selectedModules.filter(m => m !== 'rental'));
                        }
                      }}
                    />
                  }
                  label="Noleggi"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedModules.includes('accounting')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedModules([...selectedModules, 'accounting']);
                        } else {
                          setSelectedModules(selectedModules.filter(m => m !== 'accounting'));
                        }
                      }}
                    />
                  }
                  label="Contabilità"
                />
              </FormGroup>
              {selectedModules.length === 5 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Sconto 15% applicato (tutti i moduli selezionati)
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenModulesDialog(false)}>Annulla</Button>
              <Button
                onClick={async () => {
                  try {
                    await api.put(`/companies-stats/${editingCompanyModules?.id}/modules`, {
                      modules: selectedModules
                    });
                    setSnackbar({
                      open: true,
                      message: 'Moduli aggiornati con successo',
                      severity: 'success'
                    });
                    setOpenModulesDialog(false);
                    // Ricarica le statistiche
                    const response = await api.get('/companies-stats');
                    setCompaniesStats(response.data);
                  } catch (err) {
                    setSnackbar({
                      open: true,
                      message: err.response?.data?.msg || 'Errore nell\'aggiornamento moduli',
                      severity: 'error'
                    });
                  }
                }}
                variant="contained"
                color="primary"
                disabled={selectedModules.length === 0}
              >
                Salva
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Dialog unificato per aggiungere/modificare utenti */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentUser ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={currentUser ? "Nuova Password (vuoto = nessuna modifica)" : "Password"}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                required={!currentUser}
                helperText={currentUser ? "Lascia vuoto per mantenere la password attuale" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Numero di Telefono"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                helperText="Es: 3331234567 o +393331234567"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Ruolo</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  label="Ruolo"
                  onChange={handleInputChange}
                >
                  {getAvailableRoles().map((role) => (
                    <MenuItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Azienda</InputLabel>
                <Select
                  name="companyId"
                  value={formData.companyId}
                  label="Azienda"
                  onChange={handleInputChange}
                  disabled={user?.role === 'admin'}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Pulsante per aggiungere una nuova azienda */}
              {(user?.role === 'superadmin' || user?.role === 'supersaiyan') && (
                <Button
                  startIcon={<BusinessIcon />}
                  onClick={handleOpenCompanyDialog}
                  sx={{ mt: 1 }}
                  size="small"
                >
                  Nuova Azienda
                </Button>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  name="active"
                  value={formData.active}
                  label="Stato"
                  onChange={handleInputChange}
                >
                  <MenuItem value={true}>Attivo</MenuItem>
                  <MenuItem value={false}>Disattivato</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Data Scadenza"
                type="date"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Lascia vuoto per credenziali senza scadenza"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.freewheelMode || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, freewheelMode: e.target.checked }))}
                    name="freewheelMode"
                    color="primary"
                  />
                }
                label="Modalità FreeWheel (tema blu royal)"
              />
            </Grid>

            {/* Sezione Permessi - Solo se non è superadmin/supersaiyan */}
            {!['superadmin', 'supersaiyan'].includes(formData.role) && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Permessi di Accesso alle Sezioni
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  Seleziona le sezioni a cui questo utente può accedere. Superadmin e Supersaiyan hanno sempre accesso completo.
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessRentals || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessRentals: e.target.checked }))}
                          name="accessRentals"
                          color="primary"
                        />
                      }
                      label="Noleggi"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessMaintenance || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessMaintenance: e.target.checked }))}
                          name="accessMaintenance"
                          color="primary"
                        />
                      }
                      label="Manutenzioni"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessSales || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessSales: e.target.checked }))}
                          name="accessSales"
                          color="primary"
                        />
                      }
                      label="Vendite"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessShop || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessShop: e.target.checked }))}
                          name="accessShop"
                          color="primary"
                        />
                      }
                      label="Shop"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessAccounting || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessAccounting: e.target.checked }))}
                          name="accessAccounting"
                          color="primary"
                        />
                      }
                      label="Contabilità"
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSaveUser} color="primary" variant="contained">
            {currentUser ? 'Salva Modifiche' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Elimina Utente */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare l'utente <strong>{currentUser?.name}</strong>?
            Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per creare una nuova azienda */}
      <Dialog open={openCompanyDialog} onClose={handleCloseCompanyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nuova Azienda</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Azienda"
                name="newCompanyName"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                required
                error={!!companyError}
                helperText={companyError}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompanyDialog}>Annulla</Button>
          <Button onClick={handleAddCompany} color="primary" variant="contained">
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per modificare aziende */}
      <Dialog open={openCompanyEditDialog} onClose={handleCloseCompanyEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentCompany ? 'Modifica Azienda' : 'Nuova Azienda'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Azienda"
                name="name"
                value={companyFormData.name}
                onChange={handleCompanyInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rank</InputLabel>
                <Select
                  name="rank"
                  value={companyFormData.rank}
                  label="Rank"
                  onChange={handleCompanyInputChange}
                >
                  <MenuItem value={1}>Rank 1 - Premium (Miglior prezzo)</MenuItem>
                  <MenuItem value={2}>Rank 2</MenuItem>
                  <MenuItem value={3}>Rank 3</MenuItem>
                  <MenuItem value={4}>Rank 4</MenuItem>
                  <MenuItem value={5}>Rank 5 - Standard (Prezzo base)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={companyFormData.active}
                    onChange={(e) => setCompanyFormData(prev => ({ ...prev, active: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Azienda Attiva"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Informazioni Azienda e Fatturazione
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Indirizzo"
                name="address"
                value={companyFormData.address}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Città"
                name="city"
                value={companyFormData.city}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CAP"
                name="postalCode"
                value={companyFormData.postalCode}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Provincia"
                name="province"
                value={companyFormData.province}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Telefono"
                name="phone"
                value={companyFormData.phone}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Azienda"
                type="email"
                name="email"
                value={companyFormData.email}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Partita IVA"
                name="vatNumber"
                value={companyFormData.vatNumber}
                onChange={handleCompanyInputChange}
                required
                helperText="La partita IVA deve essere univoca"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Indirizzo di Spedizione
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome Destinatario"
                name="shippingName"
                value={companyFormData.shippingName}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Indirizzo di Spedizione"
                name="shippingAddress"
                value={companyFormData.shippingAddress}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Città di Spedizione"
                name="shippingCity"
                value={companyFormData.shippingCity}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="CAP di Spedizione"
                name="shippingPostalCode"
                value={companyFormData.shippingPostalCode}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Provincia di Spedizione"
                name="shippingProvince"
                value={companyFormData.shippingProvince}
                onChange={handleCompanyInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telefono di Spedizione"
                name="shippingPhone"
                value={companyFormData.shippingPhone}
                onChange={handleCompanyInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompanyEditDialog}>Annulla</Button>
          <Button onClick={handleSaveCompany} color="primary" variant="contained">
            {currentCompany ? 'Salva Modifiche' : 'Crea Azienda'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per le notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersManagement; 